import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, Loader2, Wrench, AlertTriangle, Clock,
  PauseCircle, ShieldCheck, CreditCard, Thermometer, Zap, Sun,
  RefreshCw,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatDate } from '@/utils/formatters';

interface Complaint {
  id: string;
  ticketNumber: string;
  customerName: string;
  customerPhone: string;
  productType: string;
  sopStage: string;
  priority: string;
  technicianName?: string | null;
  createdAt: string;
  isOnHold?: boolean;
}

interface DashboardStats {
  totalActive: number;
  onHold: number;
  escalated: number;
  inWarranty: number;
  paidService: number;
  byProductType: { ss_water_tank: number; heat_pump: number; swh: number };
}

const STAGE_COLORS: Record<string, string> = {
  intake: 'bg-gray-100 text-gray-700',
  hold: 'bg-orange-100 text-orange-700',
  warranty_check: 'bg-blue-100 text-blue-700',
  paid_service: 'bg-amber-100 text-amber-700',
  ticket_created: 'bg-indigo-100 text-indigo-700',
  pre_visit: 'bg-purple-100 text-purple-700',
  diagnosis: 'bg-yellow-100 text-yellow-700',
  on_site: 'bg-green-100 text-green-700',
  post_repair: 'bg-teal-100 text-teal-700',
  closure: 'bg-blue-100 text-blue-700',
  warranty_claim: 'bg-pink-100 text-pink-700',
  escalated: 'bg-red-100 text-red-700',
  closed: 'bg-gray-100 text-gray-500',
};

const STAGE_LABELS: Record<string, string> = {
  intake: 'Intake',
  hold: 'On Hold',
  warranty_check: 'Warranty Check',
  paid_service: 'Paid Service',
  ticket_created: 'Ticket Created',
  pre_visit: 'Pre-Visit',
  diagnosis: 'Diagnosis',
  on_site: 'On-Site',
  post_repair: 'Post-Repair',
  closure: 'Closure',
  warranty_claim: 'Warranty Claim',
  escalated: 'Escalated',
  closed: 'Closed',
};

const PRIORITY_COLORS: Record<string, string> = {
  High: 'bg-red-100 text-red-700',
  Critical: 'bg-red-200 text-red-800',
  Medium: 'bg-yellow-100 text-yellow-700',
  Normal: 'bg-gray-100 text-gray-600',
  Low: 'bg-gray-100 text-gray-500',
};

const PRODUCT_TYPE_LABELS: Record<string, string> = {
  ss_water_tank: 'SS Water Tank',
  heat_pump: 'Heat Pump',
  swh: 'Solar Water Heater',
};

const PRODUCT_TYPE_COLORS: Record<string, string> = {
  ss_water_tank: 'bg-blue-100 text-blue-700',
  heat_pump: 'bg-orange-100 text-orange-700',
  swh: 'bg-green-100 text-green-700',
};

const SOP_STAGES = [
  'intake', 'hold', 'warranty_check', 'paid_service', 'ticket_created',
  'pre_visit', 'diagnosis', 'on_site', 'post_repair', 'closure',
  'warranty_claim', 'escalated', 'closed',
];

export function ServiceSOPDashboardPage() {
  const navigate = useNavigate();
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [filterProduct, setFilterProduct] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [filterTechnician, setFilterTechnician] = useState('');
  const [filterWarranty, setFilterWarranty] = useState('');
  const [showPlanning, setShowPlanning] = useState(false);
  const [technicians, setTechnicians] = useState<{ id: string; firstName: string; lastName: string }[]>([]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string> = {};
      if (filterProduct) params.productType = filterProduct;
      if (filterPriority) params.priority = filterPriority;
      if (filterStage) params.sopStage = filterStage;
      if (filterTechnician) params.assignedToId = filterTechnician;
      if (filterWarranty) params.warrantyStatus = filterWarranty;
      if (search) params.search = search;
      if (showPlanning) {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        params.scheduledDate = tomorrow.toISOString().split('T')[0];
      }

      const [dashRes, listRes, techRes] = await Promise.all([
        api.get('/service-sop/dashboard'),
        api.get('/service-sop/complaints', { params }),
        api.get('/service-sop/technicians'),
      ]);

      const dashData = dashRes.data.data ?? dashRes.data;
      setStats(dashData.stats ?? {
        totalActive: 0, onHold: 0, escalated: 0, inWarranty: 0, paidService: 0,
        byProductType: { ss_water_tank: 0, heat_pump: 0, swh: 0 },
      });

      const listData = listRes.data.data ?? listRes.data;
      setComplaints(Array.isArray(listData) ? listData : listData.complaints ?? []);

      const techData = techRes.data.data ?? techRes.data;
      setTechnicians(Array.isArray(techData) ? techData : []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [filterProduct, filterPriority, filterStage, filterTechnician, filterWarranty, search, showPlanning]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">Service SOP Dashboard</h1>
          <p className="text-sm text-[var(--text-muted)]">Sun Stellar — Water Heater Service Operations</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchData}
            className="flex items-center gap-2 rounded-lg border border-[var(--border-primary)] px-3 py-2 text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-muted)] transition-colors"
          >
            <RefreshCw size={15} />
            Refresh
          </button>
          <button
            onClick={() => navigate('/service/intake')}
            className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] transition-colors"
          >
            <Plus size={16} />
            New Complaint
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-4">
            <div className="flex items-center gap-2 mb-1">
              <Wrench size={16} className="text-[var(--ink-0)]" />
              <span className="text-xs font-medium text-[var(--text-muted)]">Total Active</span>
            </div>
            <p className="text-2xl font-bold text-[var(--text-primary)]">{stats.totalActive}</p>
          </div>
          <div className="rounded-xl border border-orange-200 bg-orange-50 p-4">
            <div className="flex items-center gap-2 mb-1">
              <PauseCircle size={16} className="text-orange-600" />
              <span className="text-xs font-medium text-orange-600">On Hold</span>
            </div>
            <div className="flex items-center gap-2">
              <p className="text-2xl font-bold text-orange-700">{stats.onHold}</p>
              {stats.onHold > 0 && (
                <span className="rounded-full bg-orange-200 px-1.5 py-0.5 text-[10px] font-bold text-orange-800 animate-pulse">
                  REMINDER
                </span>
              )}
            </div>
          </div>
          <div className="rounded-xl border border-red-200 bg-red-50 p-4">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle size={16} className="text-red-600" />
              <span className="text-xs font-medium text-red-600">Escalated</span>
            </div>
            <p className="text-2xl font-bold text-red-700">{stats.escalated}</p>
          </div>
          <div className="rounded-xl border border-green-200 bg-green-50 p-4">
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck size={16} className="text-green-600" />
              <span className="text-xs font-medium text-green-600">In Warranty</span>
            </div>
            <p className="text-2xl font-bold text-green-700">{stats.inWarranty}</p>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
            <div className="flex items-center gap-2 mb-1">
              <CreditCard size={16} className="text-amber-600" />
              <span className="text-xs font-medium text-amber-600">Paid Service</span>
            </div>
            <p className="text-2xl font-bold text-amber-700">{stats.paidService}</p>
          </div>
        </div>
      )}

      {/* Product Type Breakdown */}
      {stats && (
        <div className="grid grid-cols-3 gap-4">
          <div className="flex items-center gap-4 rounded-xl border border-blue-200 bg-blue-50 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
              <Thermometer size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-xs font-medium text-blue-600">SS Water Tank</p>
              <p className="text-xl font-bold text-blue-800">{stats.byProductType?.ss_water_tank ?? 0}</p>
            </div>
          </div>
          <div className="flex items-center gap-4 rounded-xl border border-orange-200 bg-orange-50 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-100">
              <Zap size={20} className="text-orange-600" />
            </div>
            <div>
              <p className="text-xs font-medium text-orange-600">Heat Pump</p>
              <p className="text-xl font-bold text-orange-800">{stats.byProductType?.heat_pump ?? 0}</p>
            </div>
          </div>
          <div className="flex items-center gap-4 rounded-xl border border-green-200 bg-green-50 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
              <Sun size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-xs font-medium text-green-600">Solar Water Heater</p>
              <p className="text-xl font-bold text-green-800">{stats.byProductType?.swh ?? 0}</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-4">
        <div className="relative flex-1 min-w-48">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <input
            type="text"
            placeholder="Search by customer, ticket#..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] pl-9 pr-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
          />
        </div>
        <select
          value={filterProduct}
          onChange={(e) => setFilterProduct(e.target.value)}
          className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none"
        >
          <option value="">All Products</option>
          <option value="ss_water_tank">SS Water Tank</option>
          <option value="heat_pump">Heat Pump</option>
          <option value="swh">Solar Water Heater</option>
        </select>
        <select
          value={filterPriority}
          onChange={(e) => setFilterPriority(e.target.value)}
          className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none"
        >
          <option value="">All Priorities</option>
          <option value="Critical">Critical</option>
          <option value="High">High</option>
          <option value="Medium">Medium</option>
          <option value="Normal">Normal</option>
        </select>
        <select
          value={filterStage}
          onChange={(e) => setFilterStage(e.target.value)}
          className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none"
        >
          <option value="">All Stages</option>
          {SOP_STAGES.map((s) => (
            <option key={s} value={s}>{STAGE_LABELS[s]}</option>
          ))}
        </select>
        <select
          value={filterTechnician}
          onChange={(e) => setFilterTechnician(e.target.value)}
          className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none"
        >
          <option value="">All Technicians</option>
          {technicians.map((t) => (
            <option key={t.id} value={t.id}>{t.firstName} {t.lastName}</option>
          ))}
        </select>
        <select
          value={filterWarranty}
          onChange={(e) => setFilterWarranty(e.target.value)}
          className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none"
        >
          <option value="">Warranty Status</option>
          <option value="in_warranty">In Warranty</option>
          <option value="out_of_warranty">Out of Warranty</option>
          <option value="pending">Pending</option>
        </select>
        <button
          onClick={() => setShowPlanning(!showPlanning)}
          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
            showPlanning 
              ? 'bg-indigo-600 text-white' 
              : 'border border-[var(--border-primary)] text-[var(--text-secondary)] hover:bg-[var(--bg-muted)]'
          }`}
        >
          {showPlanning ? 'Showing Tomorrow\'s Plan' : 'View Tomorrow\'s Plan'}
        </button>
        {(search || filterProduct || filterPriority || filterStage) && (
          <button
            onClick={() => { setSearch(''); setFilterProduct(''); setFilterPriority(''); setFilterStage(''); }}
            className="text-sm text-[var(--ink-0)] hover:underline"
          >
            Clear
          </button>
        )}
      </div>

      {/* Table */}
      <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
            <span className="ml-2 text-sm text-[var(--text-muted)]">Loading complaints...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 text-red-500">
            <AlertTriangle size={28} className="mb-2" />
            <p className="text-sm">{error}</p>
            <button onClick={fetchData} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Retry</button>
          </div>
        ) : complaints.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-[var(--text-muted)]">
            <Wrench size={36} className="mb-2" />
            <p className="text-sm font-medium">No complaints found</p>
            <p className="text-xs">Create a new complaint to get started</p>
            <button
              onClick={() => navigate('/service/intake')}
              className="mt-3 flex items-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-3 py-1.5 text-xs font-medium text-white hover:bg-[var(--ink-1)]"
            >
              <Plus size={13} />
              New Complaint
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-[var(--border-primary)] bg-[var(--bg-muted)]">
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Ticket #</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Customer</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Product</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Stage</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Priority</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Technician</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Created</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-primary)]">
                {complaints.map((c) => (
                  <tr
                    key={c.id}
                    className="cursor-pointer hover:bg-[var(--bg-muted)] transition-colors"
                    onClick={() => navigate(`/service/complaints/${c.id}`)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-xs font-semibold text-[var(--ink-0)]">{c.ticketNumber}</span>
                        {c.isOnHold && (
                          <span className="rounded-full bg-orange-100 px-1.5 py-0.5 text-[10px] font-bold text-orange-700">HOLD</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-[var(--text-primary)]">{c.customerName}</p>
                      <p className="text-xs text-[var(--text-muted)]">{c.customerPhone}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${PRODUCT_TYPE_COLORS[c.productType] ?? 'bg-gray-100 text-gray-600'}`}>
                        {PRODUCT_TYPE_LABELS[c.productType] ?? c.productType}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STAGE_COLORS[c.sopStage] ?? 'bg-gray-100 text-gray-600'}`}>
                        {STAGE_LABELS[c.sopStage] ?? c.sopStage}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${PRIORITY_COLORS[c.priority] ?? 'bg-gray-100 text-gray-600'}`}>
                        {c.priority}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--text-secondary)]">
                      {c.technicianName ?? <span className="text-[var(--text-muted)]">Unassigned</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-[var(--text-muted)]">
                      <div className="flex items-center gap-1">
                        <Clock size={11} />
                        {formatDate(c.createdAt)}
                      </div>
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => navigate(`/service/complaints/${c.id}`)}
                        className="rounded-md bg-[var(--saffron-50)] px-2.5 py-1 text-xs font-medium text-[var(--ink-0)] hover:bg-[var(--saffron-50)] transition-colors"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
